import React from "react"
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter";
import {dracula} from "react-syntax-highlighter/dist/cjs/styles/prism";

import globalStyles from "../../../../App.module.scss";

export default function AutostartOnBoot() {
    let unitFile = `[Unit]
Description=My App
After=network.target

[Service]
Type=simple
User=julian
WorkingDirectory=/opt/my-app
ExecStart=/opt/my-app/bin/start.sh
Restart=on-failure
RestartSec=5

[Install]
WantedBy=multi-user.target`

    return (
        <div className={globalStyles.article}>
            <h1>Automate app start on boot (systemd service)</h1>

            <p>The standard way to make an app start automatically on boot on a systemd-based distro is to wrap it
                in a small <b>.service</b> unit, then enable it. This works for anything you can start from a
                command line: a Node/Python process, a Java jar, a compiled binary, a shell script.</p>

            <h3>1. Create the unit file</h3>
            <p>Custom units go in <b>/etc/systemd/system/</b>, named after the app:</p>
            <pre className={globalStyles.code}><b>root@host:~#</b> nano /etc/systemd/system/my-app.service</pre>

            <SyntaxHighlighter
                children={unitFile}
                style={dracula}
                PreTag="div"
                language={'ini'}
            />

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '330px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Key directives</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><b>Type=simple</b></td>
                        <td>ExecStart is the main process; use <b>forking</b> if the app daemonizes itself</td>
                    </tr>
                    <tr>
                        <td><b>After=network.target</b></td>
                        <td>Wait until networking is up before starting (add more units if the app needs a database,
                            e.g. <code>After=network.target postgresql.service</code>)
                        </td>
                    </tr>
                    <tr>
                        <td><b>User=julian</b></td>
                        <td>Run as a non-root user rather than the default (root)</td>
                    </tr>
                    <tr>
                        <td><b>Restart=on-failure</b></td>
                        <td>Automatically restart the process if it crashes</td>
                    </tr>
                    <tr>
                        <td><b>WantedBy=multi-user.target</b></td>
                        <td>Which boot target pulls this unit in — multi-user.target is the normal
                            "system is up" target for servers
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>2. Reload, enable and start</h3>
            <pre className={globalStyles.code}>
    <b>root@host:~#</b> systemctl daemon-reload <i># pick up the new unit file</i><br/>
    <b>root@host:~#</b> systemctl enable --now my-app <i># start now, and on every future boot</i><br/>
            </pre>

            <h3>3. Verify</h3>
            <pre className={globalStyles.code}>
    <b>root@host:~#</b> systemctl status my-app<br/>
    <b>root@host:~#</b> journalctl -u my-app -f <i># follow its logs</i><br/>
    <b>root@host:~#</b> systemctl is-enabled my-app <i># confirm it will survive a reboot</i><br/>
            </pre>

            <p>To test the boot behaviour without actually rebooting, stop the service and rely on
                <code> enable</code> having registered it, or simply reboot the box and check
                <code> systemctl status my-app</code> afterwards.</p>

            <p>To undo: <code>systemctl disable --now my-app</code> then remove the unit file and run
                <code> systemctl daemon-reload</code> again. See the <b>systemd cheat sheet</b> page for the full
                command reference.</p>
        </div>
    )
}
